import type { Item } from '@/common/api/types/items';
import type { Schedule } from '@/common/api/types/schedules';
import type { PlanSchedulingSchema, PlanSchedulingTabAdapter } from '../types';

interface PlanItemData {
  teamId?: number | null;
  earliestStartDateTime?: string | null;
  deadlineDateTime?: string | null;
  estimationId?: number | null;
}

const PLAN_SCHEDULING_FIELD_ID_MAP: Partial<Record<keyof PlanSchedulingSchema, string>> = {
  teamId: 'team',
  earliestStartDateTime: 'earliestStartDate',
  deadlineDateTime: 'deadlineDate',
  estimationId: 'estimation',
};

export const createReadOnlyPlanSchedulingAdapter = ({
  item,
  planItem,
  schedule,
  hiddenFieldIds = [],
}: {
  item: Item;
  planItem?: PlanItemData | null;
  schedule?: Schedule;
  hiddenFieldIds?: readonly string[];
}): PlanSchedulingTabAdapter => {
  const resolveEarliestStartDateTime = (): string | undefined => {
    const fromPlan = planItem
      ? (planItem.earliestStartDateTime ?? item.earliestStartDateTime)
      : item.earliestStartDateTime;
    return fromPlan ?? schedule?.forecastedStartDateTime ?? undefined;
  };

  const resolveDeadlineDateTime = (): string | undefined => {
    const fromPlan = planItem
      ? (planItem.deadlineDateTime ?? item.deadlineDateTime)
      : item.deadlineDateTime;
    return fromPlan ?? schedule?.forecastedFinishDateTime ?? undefined;
  };

  const getFieldValue = <K extends keyof PlanSchedulingSchema>(
    field: K,
  ): PlanSchedulingSchema[K] => {
    switch (field) {
      case 'teamId':
        return ((planItem ? planItem.teamId : item.teamId) ?? null) as PlanSchedulingSchema[K];
      case 'earliestStartDateTime':
        return resolveEarliestStartDateTime() as PlanSchedulingSchema[K];
      case 'deadlineDateTime':
        return resolveDeadlineDateTime() as PlanSchedulingSchema[K];
      case 'plannedStartDateTime':
        return (item.plannedStartDateTime ?? undefined) as PlanSchedulingSchema[K];
      case 'plannedFinishDateTime':
        return (item.plannedFinishDateTime ?? undefined) as PlanSchedulingSchema[K];
      case 'estimationId':
        return ((planItem ? planItem.estimationId : item.estimationId) ??
          null) as PlanSchedulingSchema[K];
      default: {
        const _exhaustiveCheck: never = field;
        return _exhaustiveCheck;
      }
    }
  };

  const updateFieldValue = async <K extends keyof PlanSchedulingSchema>(
    _field: K,
    _value: PlanSchedulingSchema[K],
  ): Promise<void> => {};

  const isEditable = (_field: keyof PlanSchedulingSchema): boolean => {
    return false;
  };

  const isDisabled = (_field: keyof PlanSchedulingSchema): boolean => {
    return false;
  };

  const isVisible = (field: keyof PlanSchedulingSchema): boolean => {
    const fieldId = PLAN_SCHEDULING_FIELD_ID_MAP[field];
    return fieldId === undefined || !hiddenFieldIds.includes(fieldId);
  };

  const getFieldSchema = (): PlanSchedulingSchema => {
    return {
      teamId: (planItem ? planItem.teamId : item.teamId) ?? null,
      earliestStartDateTime: resolveEarliestStartDateTime(),
      deadlineDateTime: resolveDeadlineDateTime(),
      plannedStartDateTime: item.plannedStartDateTime ?? undefined,
      plannedFinishDateTime: item.plannedFinishDateTime ?? undefined,
      estimationId: (planItem ? planItem.estimationId : item.estimationId) ?? null,
    };
  };

  return {
    getFieldValue,
    updateFieldValue,
    isEditable,
    isDisabled,
    isVisible,
    getFieldSchema,
  };
};
